/**
 * FYP: a store's TikTok-style feed of short videos (d1/analytics/0033). A post
 * is what the seller manages — a caption, the products it sells and one or many
 * videos — and every video is its own card in the feed, with a deep link an ad
 * can point at. Pure functions only: the server, the dashboard and the public
 * page share them, and the unit tests read them without a browser.
 */
import { youtubeVideoId } from "./liveReplay";
import { fypSourceParam, type FypSource } from "./fypSource";

export const FYP_POST_MAX_VIDEOS = 20;
/** Videos one bulk upload takes; they are split into posts of FYP_POST_MAX_VIDEOS. */
export const FYP_BULK_MAX_VIDEOS = 60;

/** A bulk upload's videos, in order, as the posts they will be saved as. */
export function fypBulkChunks<T>(videos: readonly T[], size = FYP_POST_MAX_VIDEOS): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < videos.length; i += size) out.push(videos.slice(i, i + size));
  return out;
}
export const FYP_CAPTION_MAX = 1000;
/** Videos per page of the public feed. */
export const FYP_PAGE_SIZE = 12;
/** Published posts the public feed reads; older ones fall off the end. */
export const FYP_FEED_MAX_POSTS = 200;

/** Same alphabet as live links: nothing that reads as another letter. */
const ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
export const FYP_CODE_LENGTH = 7;
const CODE = new RegExp(`^[${ALPHABET}]{${FYP_CODE_LENGTH}}$`);

export function generateFypCode(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  let out = "";
  for (const b of random(FYP_CODE_LENGTH)) out += ALPHABET[b % ALPHABET.length];
  return out;
}

export function fypCodeOk(code: string): boolean {
  return CODE.test(code);
}

/** The video ids in what a seller pasted: one link per line (or separated by
 *  spaces or commas), each once, in their order. Lines that are not a YouTube
 *  video come back as `bad` so the editor can say which. */
export function parseYoutubeList(text: string): { ids: string[]; bad: string[] } {
  const ids: string[] = [];
  const bad: string[] = [];
  for (const part of text.split(/[\s,]+/)) {
    const s = part.trim();
    if (!s) continue;
    const id = youtubeVideoId(s);
    if (!id) bad.push(s);
    else if (!ids.includes(id)) ids.push(id);
  }
  return { ids, bad };
}

export type FeedPost = { id: string; pinned: boolean; publishedAt: string | null; codes: string[] };

/** The order a viewer scrolls: pinned posts first, then the newest. Opened on a
 *  video's deep link (an ad), that video comes first, then the rest of its post,
 *  then everything else. An unknown start is ignored. */
export function feedOrder(posts: readonly FeedPost[], start?: string | null): string[] {
  const sorted = [...posts].sort(
    (a, b) =>
      Number(b.pinned) - Number(a.pinned) ||
      (b.publishedAt ?? "").localeCompare(a.publishedAt ?? "") ||
      a.id.localeCompare(b.id),
  );
  const home = start ? sorted.find((p) => p.codes.includes(start)) : undefined;
  if (!home || !start) return sorted.flatMap((p) => p.codes);
  const i = home.codes.indexOf(start);
  return [...home.codes.slice(i), ...home.codes.slice(0, i), ...sorted.filter((p) => p !== home).flatMap((p) => p.codes)];
}

const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/**
 * Where Beli takes a viewer: the product's checkout, carrying the ad that
 * brought them. The feed's own address holds the ad's utm_* and fbclid, not the
 * checkout frame, so they are passed on as they are; only a visit with no
 * campaign at all is marked as the feed's own. `fyp` names the video; a link
 * from the profile's Shop names none (code null: utm_content=profil), so no
 * video is credited with what it sells.
 */
export function fypCheckoutUrl(
  path: string,
  slug: string,
  code: string | null,
  pageSearch: string,
  arm?: "c" | "r" | null,
  src?: FypSource | null,
  texts?: { cap: string | null; head: string | null } | null,
): string {
  const url = new URL(path, "https://fyp.invalid");
  const from = new URLSearchParams(pageSearch);
  if (UTM.some((k) => from.get(k))) {
    for (const k of UTM) {
      const v = from.get(k);
      if (v) url.searchParams.set(k, v);
    }
  } else {
    url.searchParams.set("utm_source", "fyp");
    url.searchParams.set("utm_medium", "fyp");
    url.searchParams.set("utm_campaign", `fyp_${slug}`);
    url.searchParams.set("utm_content", code ?? "profil");
  }
  const fbclid = from.get("fbclid");
  if (fbclid) url.searchParams.set("fbclid", fbclid);
  if (code) url.searchParams.set("fyp", code);
  // The order engine's group (0045), so the order counts for the right one.
  if (code && arm) url.searchParams.set("fr", arm);
  // Where the visitor came to the feed from (109O, 0048).
  if (code && src) url.searchParams.set("fs", fypSourceParam(src));
  // The text options the viewer got (109O S4): caption.headline, '-' for none.
  if (code && texts && (texts.cap || texts.head)) url.searchParams.set("ft", `${texts.cap ?? "-"}.${texts.head ?? "-"}`);
  return url.pathname + url.search;
}

export function fypPath(slug: string, code?: string | null): string {
  return code ? `/fyp/${slug}/${code}` : `/fyp/${slug}`;
}

/** The way back from a checkout the gateway took the whole tab for: only ever
 *  a feed on this site, never an address from elsewhere. */
export function isFypReturnPath(path: string): boolean {
  return /^\/fyp\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]{7})?$/.test(path) && path.length <= 60;
}

/** A YouTube video's public still, for the card before its player loads. */
export function youtubePoster(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

export const FYP_COMMENT_MAX = 300;
export const FYP_COMMENT_PAGE = 30;

export type CommentProblem = "empty" | "long" | "link";

export const COMMENT_PROBLEM_TEXT: Record<CommentProblem, string> = {
  empty: "Tulis komen dahulu.",
  long: `Komen maksimum ${FYP_COMMENT_MAX} aksara.`,
  link: "Pautan tidak dibenarkan dalam komen.",
};

/** A comment the feed takes: something to say, not too long, and no links — a
 *  feed full of ad traffic is where spam goes first. */
export function commentProblem(body: string): CommentProblem | null {
  const s = body.trim();
  if (!s) return "empty";
  if (s.length > FYP_COMMENT_MAX) return "long";
  if (/https?:\/\/|www\.|\b[a-z0-9-]+\.(com|net|org|my|xyz|link|ly|io|co|me|shop|store|site|win)\b/i.test(s)) return "link";
  return null;
}

const MONTHS = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];
const MYT_MS = 8 * 3600_000;

/**
 * When a comment or a buyer's review was written, the way TikTok says it: how
 * long ago within the week ("3 jam lalu", "Semalam", "4 hari lalu"), its date
 * after that ("11 Okt", "11 Okt 2025" from another year). In words, never
 * "3h": a viewer reads that as hours. The date is Malaysia's, the day the
 * buyer wrote it.
 */
export function commentTime(iso: string, nowMs = Date.now()): string {
  // JavaScript's date-time format has milliseconds; Postgres writes microseconds.
  const at = Date.parse(iso.replace(/(\.\d{3})\d+/, "$1"));
  if (!Number.isFinite(at)) return "";
  const s = Math.max(0, (nowMs - at) / 1000);
  if (s < 60) return "Baru tadi";
  if (s < 3600) return `${Math.floor(s / 60)} minit lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  const days = Math.floor(s / 86400);
  if (days === 1) return "Semalam";
  if (days < 7) return `${days} hari lalu`;
  const d = new Date(at + MYT_MS);
  const date = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return d.getUTCFullYear() === new Date(nowMs + MYT_MS).getUTCFullYear() ? date : `${date} ${d.getUTCFullYear()}`;
}

/** 950, 1.2K, 3.4M: counts the way TikTok shows them. */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}
